/* @img-migrated */
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  Calendar, Clock, MapPin, ArrowLeft, Ticket, Info, Play, ExternalLink,
  Users, Star, ChevronLeft, Newspaper, Youtube, Share2
} from 'lucide-react';
import { Button } from '../components/ui/button';

import Img from '../components/Img';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Formatter that handles single-day, same-month-range, and cross-month-range.
export const formatEventDateRange = (startIso, endIso) => {
  if (!startIso) return '';
  const start = new Date(startIso + 'T12:00:00');
  if (Number.isNaN(start.getTime())) return startIso;
  if (!endIso || endIso === startIso) {
    return start.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }
  const end = new Date(endIso + 'T12:00:00');
  if (Number.isNaN(end.getTime())) {
    return start.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }
  const sameYear = start.getFullYear() === end.getFullYear();
  const sameMonth = sameYear && start.getMonth() === end.getMonth();
  if (sameMonth) {
    const monthYear = start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const [month, year] = monthYear.split(' ');
    return `${month} ${start.getDate()}–${end.getDate()}, ${year}`;
  }
  return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: sameYear ? undefined : 'numeric' })} – ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
};

// Classify an event as upcoming / ongoing / past based on today's date.
export const getEventStatus = (startIso, endIso) => {
  if (!startIso) return 'unknown';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(startIso + 'T00:00:00');
  const end = endIso ? new Date(endIso + 'T23:59:59') : new Date(startIso + 'T23:59:59');
  if (today < start) return 'upcoming';
  if (today > end) return 'past';
  return 'ongoing';
};

const STATUS_META = {
  upcoming: { label: 'Upcoming', tone: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  ongoing:  { label: 'Happening now', tone: 'bg-amber-100 text-amber-700 border-amber-200' },
  past:     { label: 'Past event', tone: 'bg-slate-100 text-slate-600 border-slate-200' },
};

// Extract a YouTube embed URL from common YT link shapes.
const getYoutubeEmbed = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtu.be')) return `https://www.youtube.com/embed${u.pathname}`;
    if (u.hostname.includes('youtube.com')) {
      const id = u.searchParams.get('v');
      if (id) return `https://www.youtube.com/embed/${id}`;
      if (u.pathname.startsWith('/embed/')) return url;
    }
  } catch {}
  return null;
};

const EventDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvent = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/events/${id}`);
        setEvent(res.data);
      } catch { setEvent(null); }
      finally { setLoading(false); }
    };
    fetchEvent();
  }, [id]);

  const dateRange = useMemo(() => event && formatEventDateRange(event.date, event.end_date), [event]);
  const status = useMemo(() => event && getEventStatus(event.date, event.end_date), [event]);
  const ytEmbed = useMemo(() => event && getYoutubeEmbed(event.recap_youtube_url || event.video_url), [event]);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 bg-[#F7F6F2]">
        <div className="container-custom py-12">
          <div className="h-96 bg-white rounded-2xl skeleton" />
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen pt-20 bg-[#F7F6F2] flex items-center">
        <div className="container-custom text-center py-20">
          <Calendar className="w-14 h-14 text-slate-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#002249]">Event not found</h1>
          <p className="text-slate-500 mt-2">This event may have been removed or the link is incorrect.</p>
          <Link to="/events"><Button className="mt-6 bg-[#002249] hover:bg-[#001630] rounded-lg">Browse events</Button></Link>
        </div>
      </div>
    );
  }

  const statusMeta = STATUS_META[status] || STATUS_META.upcoming;
  const img = event.flyer_url || event.image_url;

  // Build the action buttons list based on available links
  const actions = [];
  if (event.ticket_url) actions.push({ label: 'Buy Tickets', href: event.ticket_url, icon: Ticket, primary: true });
  else if (event.registration_link) actions.push({ label: 'RSVP / Register', href: event.registration_link, icon: Ticket, primary: true });
  if (event.payment_link && !event.ticket_url) actions.push({ label: event.payment_platform ? `Pay via ${event.payment_platform}` : 'Pay Online', href: event.payment_link, icon: Ticket, primary: !event.registration_link });
  if (event.info_url) actions.push({ label: 'Event Information', href: event.info_url, icon: Info });
  if (event.video_url && !ytEmbed) actions.push({ label: 'Watch Video', href: event.video_url, icon: Play });
  if (event.external_url) actions.push({ label: 'Visit External Page', href: event.external_url, icon: ExternalLink });

  return (
    <div className="min-h-screen bg-[#F7F6F2] pt-16 sm:pt-20" data-testid="event-detail-page">
      {/* Hero with cover image */}
      <div className="relative bg-[#002249] text-white overflow-hidden">
        {img && (
          <div className="absolute inset-0">
            <Img variant="generic" src={img} alt="" className="w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#002249] via-[#002249]/80 to-transparent" />
          </div>
        )}
        <div className="container-custom relative z-10 py-10 sm:py-16">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-white/70 hover:text-white text-[13px] font-medium mb-5"
            data-testid="event-back"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>

          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide border ${statusMeta.tone} bg-white/90 border-white/0`}>
              {statusMeta.label}
            </span>
            {event.is_featured && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide bg-[#E63946] text-white">
                <Star className="w-3 h-3" /> Featured
              </span>
            )}
            {event.category && event.category !== 'general' && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wide bg-white/10 text-white/90 border border-white/15">
                {event.category}
              </span>
            )}
          </div>

          <h1 className="text-[28px] sm:text-4xl lg:text-5xl font-bold leading-tight max-w-3xl">
            {event.title}
          </h1>

          <div className="mt-5 grid sm:grid-cols-3 gap-3 sm:gap-4 max-w-3xl">
            <div className="flex items-start gap-2 bg-white/10 border border-white/10 rounded-lg px-3 py-2.5 backdrop-blur-sm" data-testid="event-date">
              <Calendar className="w-4 h-4 mt-0.5 flex-shrink-0 text-white/70" />
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wide text-white/60">When</p>
                <p className="text-[13px] font-semibold truncate">{dateRange}</p>
              </div>
            </div>
            <div className="flex items-start gap-2 bg-white/10 border border-white/10 rounded-lg px-3 py-2.5 backdrop-blur-sm">
              <Clock className="w-4 h-4 mt-0.5 flex-shrink-0 text-white/70" />
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wide text-white/60">Time</p>
                <p className="text-[13px] font-semibold truncate">
                  {event.time}{event.end_time ? ` – ${event.end_time}` : ''}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2 bg-white/10 border border-white/10 rounded-lg px-3 py-2.5 backdrop-blur-sm">
              <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-white/70" />
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wide text-white/60">Where</p>
                <p className="text-[13px] font-semibold truncate">{event.location}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="container-custom py-10 sm:py-14">
        <div className="grid lg:grid-cols-3 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-2 space-y-6"
          >
            {/* Description */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8">
              <h2 className="text-lg font-bold text-[#002249] mb-3">About this event</h2>
              <p className="text-slate-700 text-[15px] leading-relaxed whitespace-pre-wrap">
                {event.description}
              </p>
            </div>

            {/* Recap (past events) */}
            {status === 'past' && (event.recap || event.recap_summary || ytEmbed) && (
              <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8" data-testid="event-recap">
                <div className="flex items-center gap-2 mb-4">
                  <Newspaper className="w-5 h-5 text-[#E63946]" />
                  <h2 className="text-lg font-bold text-[#002249]">Event Recap</h2>
                </div>
                {ytEmbed && (
                  <div className="aspect-video rounded-xl overflow-hidden bg-slate-100 mb-4">
                    <iframe
                      src={ytEmbed}
                      title="Event recap video"
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                )}
                {event.recap_summary && (
                  <p className="text-slate-700 text-[15px] leading-relaxed font-medium mb-3">{event.recap_summary}</p>
                )}
                {event.recap && (
                  <p className="text-slate-700 text-[15px] leading-relaxed whitespace-pre-wrap">{event.recap}</p>
                )}
              </div>
            )}

            {/* Gallery */}
            {event.gallery_images && event.gallery_images.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8">
                <h2 className="text-lg font-bold text-[#002249] mb-4">Gallery</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                  {event.gallery_images.map((g, i) => (
                    <a key={i} href={g} target="_blank" rel="noopener noreferrer" className="aspect-square rounded-lg overflow-hidden bg-slate-100 hover:opacity-90 transition-opacity">
                      <Img variant="generic" src={g} alt={`Gallery ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </motion.div>

          {/* Right column — Actions + Links */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="space-y-4"
          >
            <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6" data-testid="event-actions">
              <h3 className="text-sm font-bold text-[#002249] uppercase tracking-wide mb-3">
                {status === 'past' ? 'Links & Resources' : 'Take Action'}
              </h3>
              {actions.length > 0 ? (
                <div className="space-y-2">
                  {actions.map((a, i) => (
                    <a
                      key={i}
                      href={a.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                      data-testid={`event-action-${i}`}
                    >
                      <Button
                        className={`w-full h-11 rounded-lg justify-between text-[14px] font-semibold ${
                          a.primary
                            ? 'bg-[#002249] hover:bg-[#001630] text-white'
                            : 'bg-white border border-slate-300 text-[#002249] hover:bg-slate-50'
                        }`}
                        variant={a.primary ? 'default' : 'outline'}
                      >
                        <span className="flex items-center gap-2">
                          <a.icon className="w-4 h-4" />
                          {a.label}
                        </span>
                        <ExternalLink className="w-3.5 h-3.5 opacity-70" />
                      </Button>
                    </a>
                  ))}
                </div>
              ) : (
                <p className="text-[13px] text-slate-500">
                  {status === 'past' ? 'No external links for this event.' : 'Registration details coming soon.'}
                </p>
              )}

              {event.max_capacity && status !== 'past' && (
                <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-2 text-[12px] text-slate-500">
                  <Users className="w-3.5 h-3.5" />
                  Capacity: {event.max_capacity} attendees
                </div>
              )}
            </div>

            {/* Share */}
            <button
              onClick={() => {
                if (navigator.share) {
                  navigator.share({ title: event.title, url: window.location.href }).catch(() => {});
                } else if (navigator.clipboard) {
                  navigator.clipboard.writeText(window.location.href);
                }
              }}
              className="w-full h-11 rounded-lg bg-white border border-slate-200 hover:border-[#002249]/40 text-[#002249] text-[14px] font-semibold flex items-center justify-center gap-2"
              data-testid="event-share"
            >
              <Share2 className="w-4 h-4" /> Share this event
            </button>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default EventDetailPage;
